import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { describeApiError } from '../errorMessage';

interface Props {
  open: boolean;
  title: string;
  message: string;
  /** Extra emphasis for a consequence the admin can't undo. */
  warning?: string;
  confirmLabel?: string;
  /** 'error' for destructive actions, 'primary' otherwise. */
  confirmColor?: 'error' | 'primary';
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Confirmation for row actions.
 *
 * Replaces window.confirm, whose buttons can't be styled — a destructive action
 * needs to look destructive, and a native prompt reached from a red delete icon
 * gives no such signal.
 */
const ConfirmDialog: React.FC<Props> = ({
  open,
  title,
  message,
  warning,
  confirmLabel = 'Confirm',
  confirmColor = 'primary',
  onClose,
  onConfirm,
}) => {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err: any) {
      setError(describeApiError(err, 'Action failed'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">{message}</Typography>
        {warning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {warning}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isBusy} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          disabled={isBusy}
          variant="contained"
          color={confirmColor}
        >
          {isBusy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
